import { useState, useEffect } from 'react';
import { FaixaResumoEvento } from '../components/common/EventoPublico';
import { PainelEntrar } from '../components/auth/PainelEntrar';
import { statusCampeonato } from '../lib/statusCampeonato';
import { formatarDataBR } from '../lib/datas';
import { useTheme } from '../contexts/ThemeContext';
import { Link, useParams } from 'react-router-dom';
import { publicApi, fileApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PublicAthleteList } from '../components/PublicAthleteList';
import { ThemeToggle } from '../components/ThemeToggle';
import { FiltrosEventos } from '../components/publico/FiltrosEventos';
import { EstadoVazio } from '../components/common/Estados';
import {
  Trophy, Loader2, Calendar, MapPin, Users, Award, Medal,
  ArrowLeft, RefreshCw, ChevronLeft, ChevronRight, Shield,
  Zap, BarChart3, Clock, UserCircle, Layout, Globe, Lock,
  DollarSign, CreditCard, Tag, Crown, CheckCircle, GitBranch,
  Scale, BookOpen, Camera, Heart, CalendarDays, ArrowRight, FileText, Search
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';  // same-origin by default
const HERO_IMAGE = "https://images.unsplash.com/photo-1644594570497-f212163a397c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxicmF6aWxpYW4lMjBqaXUlMjBqaXRzdSUyMGNvbXBldGl0aW9uJTIwZGFyayUyMGFyZW5hfGVufDB8fHx8MTc3NjA0NjY5Nnww&ixlib=rb-4.1.0&q=85";
const ITEMS_PER_PAGE = 40;

// ======================== VERTICAL TABS CONFIG ========================
const detailTabs = [
  { key: 'registration_values', label: 'Valores das Inscricoes', icon: DollarSign },
  { key: 'payment_methods', label: 'Formas de Pagamento', icon: CreditCard },
  { key: 'prizes', label: 'Premiacao', icon: Award },
  { key: 'categories_info', label: 'Categorias', icon: Tag },
  { key: 'absolute_info', label: 'Absoluto', icon: Crown },
  { key: 'checkin_info', label: 'Checagem', icon: CheckCircle },
  { key: 'brackets_info', label: 'Chaves', icon: GitBranch },
  { key: 'weigh_in_info', label: 'Pesagem', icon: Scale },
  { key: 'rules', label: 'Regras', icon: BookOpen },
  { key: 'image_rights', label: 'Direito de Imagem', icon: Camera },
  { key: 'health_declaration', label: 'Declaracao de Saude', icon: Heart },
  { key: 'schedule', label: 'Cronograma', icon: CalendarDays },
];

function getDaysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
  return diff;
}

function formatDatePT(dateStr) {
  if (!dateStr) return '';
  const days = ['Domingo', 'Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];
  const months = ['Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  const d = new Date(dateStr + 'T12:00:00');
  return `${days[d.getDay()]}, ${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
}

// ======================== PUBLIC RESULTS / DETAIL PAGE ========================
export function PublicResultsPage() {
  const { id } = useParams();
  const [championship, setChampionship] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await publicApi.getResults(id);
        setChampionship(data.championship);
      } catch (error) {
        toast.error('Erro ao carregar campeonato');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Auto-select first tab that has content
  useEffect(() => {
    if (championship && !activeTab) {
      const first = detailTabs.find(t => championship[t.key]);
      if (first) setActiveTab(first.key);
      else setActiveTab(detailTabs[0].key);
    }
  }, [championship]);

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>;
  }

  if (!championship) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center"><Trophy className="w-16 h-16 text-[#71717A] mx-auto mb-4" /><h2 className="text-xl font-bold text-white">Campeonato nao encontrado</h2></div>
      </div>
    );
  }

  const c = championship;
  const daysUntil = getDaysUntil(c.date);

  /* ETAPA 11 — a linha de contagem que fica embaixo do Local.
     Cobre os DOIS SENTIDOS: quanto FALTA e quanto TEMPO PASSOU. Antes ela só
     valia para data futura (`daysUntil > 0`), e era por isso que a linha SUMIA
     em campeonato já realizado: o campeonato de teste, de 22/09, dá -11 e o
     filtro `>= 0` escondia a linha. Página de evento passado continua acessível,
     então ela precisa dizer alguma coisa. */
  const contagem = (() => {
    if (daysUntil === null) return null;
    if (daysUntil > 1) return { rotulo: 'Faltam', valor: `${daysUntil} dias` };
    if (daysUntil === 1) return { rotulo: 'Falta', valor: '1 dia' };
    if (daysUntil === 0) return { rotulo: null, valor: 'É hoje!' };
    const passados = Math.abs(daysUntil);
    if (passados === 1) return { rotulo: null, valor: 'Foi ontem' };
    return { rotulo: 'Há', valor: `${passados} dias` };
  })();

  const activeTabInfo = detailTabs.find(t => t.key === activeTab);
  const tabContent = c[activeTab] || '';

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-[#27272A]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/public" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white text-sm">
            <ArrowLeft size={16} /> Voltar
          </Link>
          <Link to="/public" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center">
              <Trophy className="w-4 h-4 text-black" />
            </div>
            <span className="font-bold text-white font-['Chivo'] text-sm">BJJ Manager</span>
          </Link>
          <Link to="/#entrar">
            <Button size="sm" className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold text-xs px-4">Entrar</Button>
          </Link>
        </div>
      </header>

      <main className="pt-14">
        {/* ==================== BANNER + INFO ==================== */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col lg:flex-row items-start gap-6 lg:gap-8" data-testid="championship-public-detail">
            {/* Banner — RETANGULAR, com a proporção do bloco de referência
                (≈3:2) e largura fixa, para os detalhes ocuparem o resto da
                linha ao lado. Antes era `lg:w-1/2` com `aspect-[16/10]`: um
                retângulo que tomava METADE da largura da tela.
                A imagem preenche o quadro (`object-cover`), então uma foto de
                outra proporção é recortada ao centro, sem distorcer. */}
            <div className="w-64 sm:w-80 lg:w-96 aspect-[3/2] shrink-0 rounded-xl overflow-hidden bg-[#141414] border border-[#27272A] flex items-center justify-center">
              {c.banner_path ? (
                <img
                  src={`${API_URL}/api/files/${c.banner_path}`}
                  alt={c.name}
                  className="w-full h-full object-cover"
                  data-testid="champ-banner"
                />
              ) : (
                /* Sem banner: o placeholder acompanha o tamanho do quadro. */
                <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] p-4 text-center">
                  <Trophy className="w-10 h-10 text-[#27272A] mb-3" />
                  <h2 className="text-sm font-bold text-white font-['Chivo'] leading-tight line-clamp-2">{c.name}</h2>
                </div>
              )}
            </div>

            {/* Event Info — ocupa o espaço restante, AO LADO da imagem */}
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              {/* A contagem ("faltam X dias") NÃO fica mais aqui em cima, em
                  selo: virou uma LINHA embaixo do Local, junto de data e local,
                  com o mesmo ícone dourado e o mesmo tamanho. O cálculo é o
                  mesmo (`daysUntil`), só mudou de lugar. */}

              <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Chivo'] mb-6">{c.name}</h1>

              {/* ETAPA VISUAL 6 — os 3 blocos com ladrilho de ícone de 40px (data,
                  local e situação) saíram daqui: eram ~200px de altura empilhada
                  para 3 informações curtas. Data e local viraram a FAIXA DE
                  RESUMO de largura total, logo abaixo deste bloco.
                  O selo usa `lib/statusCampeonato.js` — a MESMA fonte do V3.
                  Antes esta tela pintava "inscrições abertas" de AZUL enquanto o
                  resto do sistema pinta de VERDE. */}
              <div>
                <span className={statusCampeonato(c.status).selo}>
                  {statusCampeonato(c.status).rotulo}
                </span>
              </div>

              {/* ETAPA 11 — os quatro itens do evento ficam AQUI, ao lado da
                  imagem e UM EMBAIXO DO OUTRO (`direcao="coluna"`): nome,
                  situação, data e local. `moldura={false}` deixa data e local
                  SOLTOS, no mesmo plano do nome e da situação — antes vinham
                  dentro de uma caixa com borda e fundo.
                  Só entram os campos que a API pública devolve com certeza
                  (`date`, `address`, `event_location`). */}
              <FaixaResumoEvento
                className="mt-5"
                direcao="coluna"
                moldura={false}
                itens={[
                  { icone: Calendar, rotulo: 'Data', valor: formatDatePT(c.date) },
                  {
                    icone: MapPin,
                    rotulo: 'Local',
                    /* O endereço vira LINK DO GOOGLE MAPS quando há endereço
                       utilizável. A busca usa só o `address`: o
                       `event_location` costuma ser um complemento interno
                       ("Tatame 2", "Portão B") que atrapalharia a consulta. */
                    valor: (() => {
                      const texto = [c.address, c.event_location].filter(Boolean).join(' · ');
                      const endereco = (c.address || '').trim();
                      if (endereco.length < 5) return texto;
                      return (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(endereco)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline decoration-dotted underline-offset-2 transition-colors hover:text-[#D4AF37]"
                          data-testid="local-mapa"
                          title="Abrir no Google Maps"
                        >
                          {texto}
                        </a>
                      );
                    })(),
                  },
                  /* "Faltam X dias" (ou "Há X dias", se já passou) — logo abaixo
                     do Local, com o MESMO ícone dourado e o mesmo tamanho (16px)
                     dos outros dois. O cálculo já existia nesta tela (`daysUntil`);
                     o que mudou foi o lugar e o formato. A montagem do texto está
                     em `contagem`, acima do return. */
                  ...(contagem ? [{ icone: Clock, ...contagem }] : []),
                ]}
              />
            </div>
          </div>
        </section>

        {/* ==================== ACTION BUTTONS ==================== */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/#entrar" className="flex-1 sm:flex-initial">
              <Button variant="outline" className="w-full sm:w-auto border-[#27272A] text-white hover:bg-[#141414] font-medium px-8 py-3 h-auto" data-testid="public-login-btn">
                Ja tenho uma conta
              </Button>
            </Link>
            <Link to="/#criar-conta" className="flex-1 sm:flex-initial">
              <Button className="w-full sm:w-auto bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold px-8 py-3 h-auto" data-testid="public-register-btn">
                Inscrever-se Agora <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to={`/public/${id}/athletes`} className="flex-1 sm:flex-initial">
              <Button variant="outline" className="w-full sm:w-auto border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-medium px-8 py-3 h-auto" data-testid="public-athletes-btn">
                <Users size={16} className="mr-2" /> Atletas Inscritos
              </Button>
            </Link>
            <Link to={`/public/${id}/ranking`} className="flex-1 sm:flex-initial">
              <Button variant="outline" className="w-full sm:w-auto border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-medium px-8 py-3 h-auto" data-testid="public-ranking-btn">
                <Trophy size={16} className="mr-2" /> Ranking
              </Button>
            </Link>
            {championship.edital_path && (
              <a
                href={fileApi.getUrl(championship.edital_path)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 sm:flex-initial"
              >
                <Button variant="outline" className="w-full sm:w-auto border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-medium px-8 py-3 h-auto" data-testid="public-edital-btn">
                  <FileText size={16} className="mr-2" /> Edital
                </Button>
              </a>
            )}
            {championship.weight_table_path && (
              <a
                href={fileApi.getUrl(championship.weight_table_path)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 sm:flex-initial"
              >
                <Button variant="outline" className="w-full sm:w-auto border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-medium px-8 py-3 h-auto" data-testid="public-weight-table-btn">
                  <FileText size={16} className="mr-2" /> Tabela de Pesos
                </Button>
              </a>
            )}
          </div>
        </section>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
          <div className="flex flex-col lg:flex-row gap-0 rounded-xl overflow-hidden border border-[#27272A] bg-[#141414]" data-testid="detail-tabs-container">
            {/* Vertical Sidebar Tabs */}
            <div className="lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-[#27272A] bg-[#0A0A0A]">
              <div className="flex lg:flex-col overflow-x-auto lg:overflow-x-visible">
                {detailTabs.map((tab) => {
                  const isActive = activeTab === tab.key;
                  const hasContent = !!c[tab.key];
                  const TabIcon = tab.icon;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      data-testid={`tab-${tab.key}`}
                      className={`flex items-center gap-3 px-4 py-3.5 text-left text-sm whitespace-nowrap lg:whitespace-normal transition-all w-full ${
                        isActive
                          ? 'bg-[#D4AF37] text-black font-bold'
                          : hasContent
                            ? 'text-white hover:bg-[#141414]'
                            : 'text-[#71717A] hover:bg-[#141414]/50'
                      }`}
                    >
                      <TabIcon size={16} className={isActive ? 'text-black' : hasContent ? 'text-[#D4AF37]' : 'text-[#71717A]'} />
                      <span className="truncate">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 p-6 sm:p-8 min-h-[400px]">
              {activeTabInfo && (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                      <activeTabInfo.icon size={20} className="text-[#D4AF37]" />
                    </div>
                    <h3 className="text-xl font-bold text-white font-['Chivo']">{activeTabInfo.label}</h3>
                  </div>
                  {tabContent ? (
                    <div className="text-[#A1A1AA] whitespace-pre-wrap leading-relaxed text-sm" data-testid="tab-content">
                      {tabContent}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <activeTabInfo.icon size={40} className="text-[#27272A] mb-3" />
                      <p className="text-[#71717A]">Informacao nao cadastrada para este campeonato.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        {/* About Event Section (if has about_event) */}
        {c.about_event && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
            <Card className="bg-[#141414] border-[#27272A]">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <FileText size={20} className="text-[#D4AF37]" />
                  <h3 className="text-lg font-bold text-white font-['Chivo']">Sobre o Evento</h3>
                </div>
                <p className="text-[#A1A1AA] whitespace-pre-wrap leading-relaxed text-sm">{c.about_event}</p>
              </CardContent>
            </Card>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#27272A] bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center">
              <Trophy className="w-3.5 h-3.5 text-black" />
            </div>
            <span className="text-[#71717A] text-xs">BJJ Manager &copy; {new Date().getFullYear()}</span>
          </div>
          <Link to="/public" className="text-[#71717A] hover:text-white text-xs">Todos os eventos</Link>
        </div>
      </footer>
    </div>
  );
}

// ======================== FEATURES DATA ========================
const features = [
  { icon: Trophy, title: 'Campeonatos Completos', desc: 'Crie e gerencie campeonatos com tabelas de peso, chaves automaticas e resultados em tempo real.' },
  { icon: Users, title: 'Gestao de Equipes', desc: 'Cadastre equipes, professores e atletas com perfis completos, fotos e carteirinhas digitais.' },
  { icon: Zap, title: 'Chaves Automaticas', desc: 'Geracao automatica de chaves separadas por idade, faixa, peso e sexo com sistema de BYE.' },
  { icon: Clock, title: 'Tempo Real', desc: 'Mesa Central com WebSocket para registrar resultados ao vivo. O publico acompanha tudo instantaneamente.' },
  { icon: BarChart3, title: 'Rankings e Medalhas', desc: 'Sistema de pontuacao configuravel, rankings por equipe e atleta, medalhas automaticas.' },
  { icon: Lock, title: 'Controle de Acesso', desc: 'Niveis de acesso para Admin, Federacao, Professor, Atleta e Staff com permissoes granulares.' },
  { icon: UserCircle, title: 'Carteirinha Digital', desc: 'Documento digital do atleta com QR Code para check-in rapido no dia do evento.' },
  { icon: Layout, title: 'Estrutura do Evento', desc: 'Configure tatames, juizes e areas. Organize a logistica completa do campeonato.' },
];

// ======================== LANDING PAGE ========================
export default function PublicPage() {
  const { theme } = useTheme();
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ETAPA 12 — BUSCA, FILTROS E PAGINAÇÃO (tudo no SERVIDOR)
     Antes o navegador recebia ate 100 campeonatos, filtrava e paginava sozinho —
     e do 101o em diante simplesmente NAO EXISTIA para o visitante, sem aviso.
     Agora o backend devolve so a pagina pedida, com o total, e e ele quem
     filtra e ordena. A resposta por requisicao deixa de crescer com o acervo. */
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [filtros, setFiltros] = useState({ ano: '', mes: '', modalidade: '', federacao: '', situacao: '' });
  const [pagina, setPagina] = useState(1);
  const [paginas, setPaginas] = useState(1);
  const [total, setTotal] = useState(0);
  const [opcoes, setOpcoes] = useState({ anos: [], federacoes: [] });

  /* Opcoes dos filtros: vem do BANCO, uma vez so. Nada e inventado — so aparece
     ano/federacao que realmente tem campeonato. */
  useEffect(() => {
    publicApi.getChampionshipFilters()
      .then(({ data }) => setOpcoes({
        anos: data?.anos || [],
        federacoes: data?.federacoes || [],
      }))
      .catch(() => { /* sem opcoes, restam os filtros de valor fixo (modalidade/situacao) */ });
  }, []);

  /* DEBOUNCE — sem isto, cada letra digitada viraria uma consulta ao banco. */
  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaAplicada(busca.trim());
      setPagina(1);
    }, 350);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => {
    let cancelado = false;
    const buscar = async () => {
      try {
        const { data } = await publicApi.getChampionships({
          q: buscaAplicada || undefined,
          ano: filtros.ano || undefined,
          mes: filtros.mes || undefined,
          modalidade: filtros.modalidade || undefined,
          federacao: filtros.federacao || undefined,
          situacao: filtros.situacao || undefined,
          pagina,
          por_pagina: ITEMS_PER_PAGE,
        });
        if (cancelado) return;
        /* Aceita os DOIS formatos: o novo (`{items, total, paginas}`) e o antigo
           (array puro). Assim a tela nao quebra enquanto o backend nao for
           reiniciado — a mudanca de contrato foi feita dos dois lados. */
        /* Campeonato FINALIZADO nao aparece na listagem publica — eles ficam na
           area de resultados/ranking do sistema.

           No formato NOVO quem garante isso e o backend (o padrao e
           `open` + `in_progress`), e a guarda abaixo vira no-op. Ela existe
           porque no formato ANTIGO (array) a filtragem era feita AQUI, no
           navegador — e porque essa garantia nao deve depender de qual versao
           do backend esta no ar. */
        const itens = Array.isArray(data)
          ? data.filter((c) => c.status !== 'finished')
          : (data?.items || []);
        const totalRecebido = Array.isArray(data) ? itens.length : (data?.total ?? itens.length);
        setChampionships(itens);
        setTotal(totalRecebido);
        setPaginas(Array.isArray(data) ? 1 : (data?.paginas || 1));
      } catch (error) {
        if (!cancelado) toast.error('Erro ao carregar campeonatos');
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    buscar();
    return () => { cancelado = true; };
  }, [buscaAplicada, filtros.ano, filtros.mes, filtros.modalidade, filtros.federacao, filtros.situacao, pagina]);

  const temFiltroAtivo = Boolean(
    buscaAplicada || filtros.ano || filtros.mes || filtros.modalidade || filtros.federacao || filtros.situacao
  );

  const aplicarFiltro = (chave, valor) => {
    setFiltros((prev) => ({ ...prev, [chave]: valor }));
    setPagina(1); // qualquer mudanca de filtro volta para a primeira pagina
  };

  const limparFiltros = () => {
    setBusca('');
    setBuscaAplicada('');
    setFiltros({ ano: '', mes: '', modalidade: '', federacao: '', situacao: '' });
    setPagina(1);
  };

  /* A lista ja vem filtrada, ordenada e paginada pelo servidor. */
  const pagedChamps = championships;

  /* ETAPA VISUAL 6 — `statusLabel`/`statusColor` locais foram removidos: eles
     pintavam "inscrições abertas" de AZUL, enquanto `lib/statusCampeonato.js`
     (usado em todo o sistema desde o V3) pinta de VERDE. Agora os dois usam a
     mesma fonte — a cor significa a mesma coisa na vitrine e dentro do sistema. */

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* ==================== HEADER ==================== */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-[#27272A]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/public" className="flex items-center gap-3" data-testid="landing-logo">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center">
              <Trophy className="w-5 h-5 text-black" />
            </div>
            <span className="font-bold text-lg text-white font-['Chivo'] hidden sm:inline">BJJ Manager</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <a href="#eventos" className="text-[#A1A1AA] hover:text-white text-sm transition-colors">Eventos</a>
            <a href="#funcionalidades" className="text-[#A1A1AA] hover:text-white text-sm transition-colors">Funcionalidades</a>
            <a href="#vantagens" className="text-[#A1A1AA] hover:text-white text-sm transition-colors">Vantagens</a>
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-3 mr-2">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-[#71717A] hover:text-[#D4AF37] transition-colors" aria-label="Instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://www.youtube.com/@e-torneios-bjjmanager" target="_blank" rel="noopener noreferrer" className="text-[#71717A] hover:text-[#D4AF37] transition-colors" aria-label="YouTube">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
            {/* Alternador de tema ao lado do "Entrar" — antes, quem chegava pela
                landing só encontrava o botão de tema depois de entrar no
                login. É o MESMO componente da tela de login (`variant="icon"`),
                então o rótulo acessível e o comportamento são idênticos. */}
            <ThemeToggle variant="icon" />
            <a href="#entrar">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold text-sm px-5" data-testid="landing-login-btn">
                Entrar
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* ==================== HERO ==================== */}
      {/* ETAPA VISUAL 6 — 2 colunas: a promessa à esquerda e um evento REAL à
          direita. Antes era uma coluna centralizada de ~740px de altura, e o
          primeiro campeonato só aparecia a ~988px (fora da primeira dobra). */}
      <section className="relative pt-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover opacity-25" />
          <div className={`absolute inset-0 bg-gradient-to-b ${
            theme === 'light'
              ? 'from-[#F7F7F5]/40 via-[#F7F7F5]/60 to-[#F7F7F5]/80'
              : 'from-[#0A0A0A]/60 via-[#0A0A0A]/80 to-[#0A0A0A]'
          }`} />
        </div>
        {/* O topo é menor que a base DE PROPÓSITO: o `pt-16` da seção (64px)
            já reserva a altura do cabeçalho fixo (`h-16`), então o padding
            interno de cima era folga dupla — 160px até o primeiro elemento. */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-16 sm:pt-10 sm:pb-20 lg:pt-12 lg:pb-24">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            {/* Coluna 1 — a promessa */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
                <span className="text-[#D4AF37] text-sm font-medium">Plataforma #1 de Campeonatos de Jiu-Jitsu</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white font-['Chivo'] leading-tight">
                Organize seus campeonatos <span className="text-[#D4AF37]">com facilidade</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
                Do cadastro dos atletas às chaves, resultados ao vivo e rankings. Tudo integrado em uma
                plataforma criada para tornar seus eventos mais profissionais, organizados e fáceis de gerenciar.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <a href="#criar-conta">
                  <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold text-base px-8 py-3 h-auto" data-testid="hero-cta">
                    Comecar Agora
                  </Button>
                </a>
                <a href="#eventos">
                  <Button variant="outline" className="border-[#27272A] text-white hover:bg-[#141414] text-base px-8 py-3 h-auto">
                    Ver Campeonatos
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-sm text-[#71717A]">
                <span className="flex items-center gap-2"><Trophy size={16} className="text-[#D4AF37]" /> Chaves automaticas</span>
                <span className="flex items-center gap-2"><Zap size={16} className="text-[#D4AF37]" /> Tempo real</span>
                <span className="flex items-center gap-2"><Globe size={16} className="text-[#D4AF37]" /> 100% online</span>
              </div>
            </div>

            {/* Coluna 2 — ETAPA 11: no lugar do card do campeonato entra o
                ACESSO. A página pública virou a página inicial, então entrar e
                criar conta acontecem aqui, embutidos (ver `PainelEntrar`), com
                exatamente a mesma lógica das telas dedicadas. */}
            {/* ETAPA 11 (ajuste) — `order-first lg:order-none`: NO CELULAR o
                acesso vem ANTES da chamada (o grid de uma coluna seguia a ordem
                do DOM, deixando o formulário lá embaixo). A partir de `lg` a
                ordem volta ao normal e o painel fica à DIREITA, como no layout
                de sempre. Só a ordem muda: nada de tamanho, cor ou espaçamento. */}
            <div className="order-first mx-auto w-full max-w-md lg:order-none lg:mx-0 lg:justify-self-end">
              <PainelEntrar />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== CHAMPIONSHIPS ==================== */}
      <section id="eventos" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-wider">Eventos</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Chivo'] mt-1">Campeonatos</h2>
          </div>
          <Link to="/public/ranking" data-testid="public-ranking-events-link">
            <Button variant="outline" size="sm" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-medium">
              <BarChart3 size={16} className="mr-1.5" /> Ranking dos Eventos
            </Button>
          </Link>
          {paginas > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => setPagina((p) => Math.max(1, p - 1))}
                disabled={pagina === 1}
                className="border-[#27272A] text-[#A1A1AA] hover:text-white disabled:opacity-30"
                data-testid="prev-page"
              >
                <ChevronLeft size={16} />
              </Button>
              <span className="text-[#71717A] text-sm px-2">{pagina} / {paginas}</span>
              <Button
                variant="outline" size="sm"
                onClick={() => setPagina((p) => Math.min(paginas, p + 1))}
                disabled={pagina >= paginas}
                className="border-[#27272A] text-[#A1A1AA] hover:text-white disabled:opacity-30"
                data-testid="next-page"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          )}
        </div>

        {/* ETAPA 12 — busca em destaque + filtros discretos atrás do botão */}
        <FiltrosEventos
          busca={busca}
          aoBuscar={setBusca}
          filtros={filtros}
          aoFiltrar={aplicarFiltro}
          aoLimpar={limparFiltros}
          opcoes={opcoes}
        />

        {/* Quantos resultados — e, quando houver filtro, que ele está aplicado */}
        {!loading && (
          <p className="mb-4 text-sm text-[#71717A]" data-testid="contagem-campeonatos">
            {total === 0
              ? 'Nenhum campeonato encontrado'
              : `${total} campeonato${total === 1 ? '' : 's'}${temFiltroAtivo ? ' para os filtros aplicados' : ''}`}
            {paginas > 1 && ` · pagina ${pagina} de ${paginas}`}
          </p>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
        ) : championships.length === 0 ? (
          /* Dois vazios DIFERENTES: nao ha campeonato nenhum, ou os filtros nao
             devolveram nada. No segundo caso o caminho de saida fica na tela. */
          <div className="rounded-2xl bg-[#141414] border border-[#27272A]">
            {temFiltroAtivo ? (
              <EstadoVazio
                icone={Search}
                titulo="Nenhum campeonato encontrado"
                descricao="Tente outra busca ou remova algum dos filtros."
                acao={
                  <Button
                    variant="outline"
                    onClick={limparFiltros}
                    className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                    data-testid="vazio-limpar-filtros"
                  >
                    Limpar filtros
                  </Button>
                }
                testid="vazio-com-filtros"
              />
            ) : (
              <EstadoVazio
                icone={Trophy}
                titulo="Nenhum campeonato disponivel"
                descricao="Novos campeonatos aparecerao aqui"
                testid="vazio-sem-campeonatos"
              />
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="championships-grid">
            {pagedChamps.map((champ) => {
              const daysLeft = getDaysUntil(champ.date);
              const daysLabel =
                daysLeft === null ? null :
                daysLeft === 0 ? 'É hoje' :
                daysLeft === 1 ? 'É amanhã' :
                daysLeft > 1 ? `Faltam ${daysLeft} Dias` : null;
              return (
              <Link key={champ._id} to={`/public/${champ._id}`} data-testid={`champ-card-${champ._id}`}>
                <div className="group overflow-hidden bg-[#141414] border border-[#27272A] hover:border-[#D4AF37]/40 transition-all duration-300 h-full flex flex-col">
                  {/* Banner Image — sem arredondamento no card e altura agora em
                      193,6px (176px + 10%), a pedido do responsável. */}
                  <div className="relative h-[12.1rem] bg-[#1A1A1A] overflow-hidden">
                    {champ.banner_path ? (
                      <img
                        src={`${API_URL}/api/files/${champ.banner_path}`}
                        alt={champ.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A]">
                        <Trophy className="w-12 h-12 text-[#27272A]" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className={`${statusCampeonato(champ.status).selo} px-3 py-1.5`}>
                        {statusCampeonato(champ.status).rotulo}
                      </span>
                    </div>
                  </div>
                  {/* Info — espaçamentos internos 10% maiores (é o que faz o
                      CARD ficar 10% maior), mantendo FONTES e ícones como estavam. */}
                  <div className="p-[1.1rem] flex-1 flex flex-col">
                    <h3 className="font-bold text-white text-lg leading-tight mb-[0.825rem] line-clamp-2 group-hover:text-[#D4AF37] transition-colors">{champ.name}</h3>
                    <div className="mt-auto space-y-[0.55rem]">
                      {/* Data e endereço: fontes 10% menores (0,9rem em vez de
                          1rem), a pedido do responsável — só estas duas linhas. */}
                      <div className="flex items-center gap-2 text-[#71717A] text-[0.9rem]">
                        <Calendar size={16} className="text-[#D4AF37] shrink-0" />
                        <span className="truncate">{formatarDataBR(champ.date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#71717A] text-[0.9rem]">
                        <MapPin size={16} className="text-[#D4AF37] shrink-0" />
                        <span className="truncate">{champ.address}</span>
                      </div>
                      {daysLabel && (
                        <div className="flex justify-end">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/25">
                            <Clock size={12} className="text-[#D4AF37] shrink-0" />
                            <span className="text-[#D4AF37] font-bold text-micro">{daysLabel}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* ==================== FEATURES ==================== */}
      <section id="funcionalidades" className="bg-[#141414] border-y border-[#27272A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-wider">Funcionalidades</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Chivo'] mt-2">Tudo que voce precisa para organizar campeonatos</h2>
            <p className="text-[#A1A1AA] mt-3 max-w-xl mx-auto">Uma plataforma completa pensada por quem entende de Jiu-Jitsu.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f, i) => (
              <div key={i} className="p-5 rounded-xl bg-[#0A0A0A] border border-[#27272A] hover:border-[#D4AF37]/30 transition-colors group">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <f.icon size={20} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-bold text-white text-sm mb-2">{f.title}</h3>
                <p className="text-[#71717A] text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== ADVANTAGES ==================== */}
      <section id="vantagens" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-wider">Vantagens</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Chivo'] mt-2 mb-6">Por que escolher o BJJ Manager?</h2>
            <div className="space-y-5">
              {[
                { title: 'Sem planilhas, sem papel', desc: 'Substitua planilhas e anotacoes manuais por um sistema digital completo e profissional.' },
                { title: 'Resultados instantaneos', desc: 'O publico acompanha os resultados em tempo real pelo celular. Nada de esperar o quadro ser atualizado.' },
                { title: 'Chaves justas e automaticas', desc: 'Separacao por idade, faixa, peso e sexo automaticamente. Sem erros humanos na montagem das chaves.' },
                { title: 'Para federacoes e academias', desc: 'Multiplos niveis de acesso: federacao, professor, equipe, atleta. Cada um ve o que precisa.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Award size={16} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-sm">{item.title}</h4>
                    <p className="text-[#71717A] text-xs mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { value: '100%', label: 'Online e Gratuito' },
              { value: '50+', label: 'Atletas Cadastrados' },
              { value: '14', label: 'Chaves Automaticas' },
              { value: '24/7', label: 'Disponivel Sempre' },
            ].map((s, i) => (
              <div key={i} className="p-6 rounded-xl bg-[#141414] border border-[#27272A] text-center">
                <p className="text-3xl font-bold text-[#D4AF37] font-['Chivo']">{s.value}</p>
                <p className="text-[#71717A] text-xs mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="bg-[#141414] border-t border-[#27272A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Chivo'] mb-4">Pronto para organizar seu campeonato?</h2>
          <p className="text-[#A1A1AA] mb-8 max-w-lg mx-auto">Crie sua conta agora e comece a usar todas as ferramentas do BJJ Manager.</p>
          <div className="flex items-center justify-center gap-4">
            <a href="#criar-conta">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold text-base px-8 py-3 h-auto" data-testid="cta-register">
                Criar Conta Gratis
              </Button>
            </a>
            <a href="#entrar">
              <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-base px-8 py-3 h-auto">
                Ja tenho conta
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="border-t border-[#27272A] bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center">
              <Trophy className="w-4 h-4 text-black" />
            </div>
            <span className="text-[#71717A] text-sm">BJJ Manager &copy; {new Date().getFullYear()}</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-[#71717A] hover:text-[#D4AF37] transition-colors" aria-label="Instagram">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            </a>
            <a href="https://www.youtube.com/@e-torneios-bjjmanager" target="_blank" rel="noopener noreferrer" className="text-[#71717A] hover:text-[#D4AF37] transition-colors" aria-label="YouTube">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            </a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-[#71717A] hover:text-[#D4AF37] transition-colors" aria-label="Facebook">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
